/**
 * Manages Leaflet marker clustering with dynamic enable/disable and size control
 */

import L from 'leaflet';
import 'leaflet.markercluster';
import { CLUSTER_RADIUS } from './types.js';
import { getMarkerStatus } from './markers.js';

const INDIVIDUAL_MARKER_ZOOM = 14;

function createClusterIcon(cluster: L.MarkerCluster): L.DivIcon {
  const markers = cluster.getAllChildMarkers();
  const total = markers.length;
  let visited = 0;
  let closed = 0;

  for (const marker of markers) {
    const status = getMarkerStatus(marker);
    if (status === 'visited') visited++;
    if (status === 'closed') closed++;
  }

  const visitedAngle = (visited / total) * 360;
  const closedAngle = ((visited + closed) / total) * 360;
  const size = total < 10 ? 40 : total < 100 ? 46 : 52;
  const visitedPercentage = Math.round((visited / total) * 100);

  return L.divIcon({
    className: 'pub-cluster',
    html: `
      <div class="pub-cluster__ring" style="--visited-angle:${visitedAngle}deg;--closed-angle:${closedAngle}deg">
        <div class="pub-cluster__centre">
          <strong>${total}</strong>
          <span>${visitedPercentage}%</span>
        </div>
      </div>`,
    iconSize: L.point(size, size),
  });
}

/**
 * ClusterManager class - manages clustering state and layer swapping
 */
export class ClusterManager {
  private map: L.Map;
  private markers: L.Marker[] = [];
  private clusteredLayer: L.MarkerClusterGroup | null = null;
  private nonClusteredLayer: L.LayerGroup | null = null;
  private clusteringEnabled: boolean = true;

  constructor(map: L.Map, markers: L.Marker[]) {
    this.map = map;
    this.markers = markers;
  }

  /**
   * Add multiple markers to storage
   * @param markers - Array of Leaflet markers to store
   */
  addMarkers(markers: L.Marker[]): void {
    this.markers.push(...markers);
    console.log(`Added ${markers.length} markers to storage. Total: ${this.markers.length}`);
  }

  /**
   * Initialize cluster manager with user preferences
   * Creates only the initially visible layer. The alternative layer is lazy.
   * @param clusteringEnabled - Whether to show clustered view initially
   */
  initialize(clusteringEnabled: boolean): void {
    this.clusteringEnabled = clusteringEnabled;

    if (clusteringEnabled) {
      this.clusteredLayer = this.createClusterLayer();
      this.map.addLayer(this.clusteredLayer);
    } else {
      this.nonClusteredLayer = this.createNonClusterLayer();
      this.map.addLayer(this.nonClusteredLayer);
    }

    console.log(`ClusterManager initialized: clustering=${clusteringEnabled}`);
  }

  /**
   * Toggle clustering on/off
   * Swaps between clustered and non-clustered layers
   * @param enabled - True to enable clustering, false to show individual markers
   */
  setClusteringEnabled(enabled: boolean): void {
    if (enabled === this.clusteringEnabled) {
      // No change needed
      return;
    }

    this.clusteringEnabled = enabled;

    if (enabled) {
      // Switch from non-clustered to clustered
      if (this.nonClusteredLayer) {
        this.map.removeLayer(this.nonClusteredLayer);
      }
      this.clusteredLayer ??= this.createClusterLayer();
      this.map.addLayer(this.clusteredLayer);
      console.log('Clustering enabled - switched to clustered layer');
    } else {
      // Switch from clustered to non-clustered
      if (this.clusteredLayer) {
        this.map.removeLayer(this.clusteredLayer);
      }
      this.nonClusteredLayer ??= this.createNonClusterLayer();
      this.map.addLayer(this.nonClusteredLayer);
      console.log('Clustering disabled - switched to individual markers');
    }
  }

  /**
   * Create a clustered layer group using the compact fixed radius.
   * @returns MarkerClusterGroup with configured radius
   */
  private createClusterLayer(): L.MarkerClusterGroup {
    const clusterGroup = L.markerClusterGroup({
      maxClusterRadius: CLUSTER_RADIUS,
      disableClusteringAtZoom: INDIVIDUAL_MARKER_ZOOM,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      removeOutsideVisibleBounds: true,
      chunkedLoading: true,
      chunkInterval: 100,
      chunkDelay: 20,
      iconCreateFunction: createClusterIcon,
    });

    // Batch insertion lets MarkerCluster build its spatial tree efficiently.
    clusterGroup.addLayers(this.markers);

    console.log(`Created cluster layer (${CLUSTER_RADIUS}px radius)`);
    return clusterGroup;
  }

  /**
   * Create a non-clustered layer group (all individual markers)
   * @returns LayerGroup containing all markers
   */
  private createNonClusterLayer(): L.LayerGroup {
    const layerGroup = L.layerGroup(this.markers);
    console.log(`Created non-clustered layer with ${this.markers.length} individual markers`);
    return layerGroup;
  }
}
